import React from 'react';
import { AppButton, Box, Checkbox, Inscription, ProgressBar } from '@common-fe/common-fe';
import { useTheme } from 'styled-components';

import { ModalWrapper } from '@/components/wrappers';

import { useSyncUpdatesModal } from './hooks/useSyncUpdatesModal';
import SyncUpdatesGroupCard from './SyncUpdatesGroupCard';
import {
  getSyncUpdatesLabel,
  SKIP_SYNC_LABEL,
  SYNC_UPDATES_STEPS,
  SyncUpdatesStep,
} from './SyncUpdatesModal.constants';
import { StyledSmallCheckbox } from './SyncUpdatesModal.styles';
import { SyncUpdatesModalProps } from './SyncUpdatesModal.types';

const SyncUpdatesModal: React.FC<SyncUpdatesModalProps> = ({
  visible,
  changes,
  onSkipSync,
  onClose,
  onSyncSelected,
}) => {
  const theme = useTheme();
  const {
    groups,
    selected,
    expandedProperties,
    allExpanded,
    isAllSelected,
    isAllIndeterminate,
    handleToggleAll,
    handleToggleProperty,
    handleToggleGroupProperties,
    handleToggleExpandAll,
    handleFooterAction,
  } = useSyncUpdatesModal({ changes, onSkipSync, onSyncSelected });

  return (
    <ModalWrapper
      testId="sync-updates-modal"
      visible={visible}
      onSetVisible={(value) => {
        if (!value) onClose();
      }}
      isClosingByClickOutsideBlocked
      maxWidth="720px"
    >
      <Box direction="column" gap="spacing24">
        <Box align="center">
          <Box width="65%">
            <ProgressBar steps={SYNC_UPDATES_STEPS} activeKey={SyncUpdatesStep.SYNC} />
          </Box>
        </Box>

        <Box align="center">
          <Inscription
            data-testid="sync-updates-modal_header"
            size="2xl"
            color="textTitle"
            weight="bold"
            lineHeight="32px"
            textAlign="center"
          >
            Sync Updates Across Organizations
          </Inscription>
          <Box direction="column" gap="spacing8" margin={{ top: 'spacing8' }}>
            <Inscription size="medium" color="textBody" lineHeight="20px" textAlign="center">
              You've made changes that can be applied to lower-level organizations. Select which updates you'd
              like to apply across all lower-level organizations.
            </Inscription>
            <Inscription size="medium" color="textBody" lineHeight="20px" textAlign="center">
              If no updates are selected, changes will apply only to this organization and any future
              lower-level organizations. No other existing organizations will be updated.
            </Inscription>
          </Box>
        </Box>

        <Box
          round="moduleRound"
          background="module"
          direction="column"
          gap="spacing12"
          pad="spacing24"
          width="672px"
        >
          <Box direction="row" align="center">
            <Inscription weight="bold" color="textTitle" data-testid="sync-updates-modal_updates-count">
              Updates: {changes.length}
            </Inscription>
            <Box
              margin={{ left: 'spacing12' }}
              data-testid="sync-updates-modal_collapse-toggle"
              onClick={handleToggleExpandAll}
            >
              <Inscription color="textAccent" size="medium">
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Inscription>
            </Box>
          </Box>

          <Box
            round="container2Round"
            background="legend"
            direction="row"
            align="center"
            gap="spacing12"
            pad={{ vertical: '7px', right: 'spacing16', left: '64px' }}
          >
            <StyledSmallCheckbox align="center" justify="center">
              <Checkbox
                data-testid="sync-updates-modal_select-all"
                checked={isAllSelected}
                indetermiateMode={isAllIndeterminate}
                onChange={handleToggleAll}
                backgroundColor={theme.colors.canvas}
              />
            </StyledSmallCheckbox>
            <Inscription
              weight="bold"
              color="textTitle"
              data-testid="sync-updates-modal_header-property-name"
            >
              Property name
            </Inscription>
          </Box>

          {groups.map((group) => (
            <SyncUpdatesGroupCard
              key={group.section}
              group={group}
              expandedProperties={expandedProperties}
              selected={selected}
              onToggleGroupProperties={handleToggleGroupProperties}
              onToggleProperty={handleToggleProperty}
            />
          ))}
        </Box>

        <Box direction="row" justify="end">
          <AppButton data-testid="sync-updates-modal_footer-button" onClick={handleFooterAction}>
            {selected.size === 0 ? SKIP_SYNC_LABEL : getSyncUpdatesLabel(selected.size)}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default SyncUpdatesModal;
